import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase.config';
import DynamicSchedule from '../../components/DynamicSchedule';
import { ArrowLeft, ChevronLeft, ChevronRight, Monitor, Projector, Cable, SquarePen } from 'lucide-react';
import {
  MdWifi, MdPower, MdLocalCafe, MdEventSeat, MdFastfood,
  MdWork, MdAccessible
} from 'react-icons/md';
import { FaChalkboard, FaTv } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

const featureIcons = {
  Wifi: MdWifi,
  'Power Outlets': MdPower,
  Whiteboard: FaChalkboard,
  Cafe: MdLocalCafe,
  Couches: MdEventSeat,
  FoodAndDrinks: MdFastfood,
  privateDesks: MdWork,
  accessible: MdAccessible,
  projector: FaTv
};

const RoomDetails = () => {
  const { building, room } = useParams();
  const navigate = useNavigate();
  const [roomData, setRoomData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const fetchRoomData = async () => {
      try {
        const decodedBuilding = decodeURIComponent(building).trim();
        const decodedRoom = decodeURIComponent(room).trim();

        // Fetch lecture hall with building and photos from Supabase
        const { data: lectureHalls, error } = await supabase
          .from('LectureHall')
          .select(`
            *,
            building:Building(*),
            photos:Photo(*)
          `)
          .eq('room', decodedRoom);

        if (error) throw error;

        // Find the matching lecture hall by building name
        const matchingHall = lectureHalls.find(hall => 
          hall.building?.name === decodedBuilding ||
          hall.building?.name?.includes(decodedBuilding) ||
          decodedBuilding.includes(hall.building?.name || '')
        );

        if (matchingHall) {
          // Transform to expected format with legacy compatibility
          setRoomData({
            id: matchingHall.id,
            building: matchingHall.building?.name || decodedBuilding,
            room: matchingHall.room,
            campus: 'Waterloo Campus',
            photos: matchingHall.photos?.map(p => p.url) || [],
            hasAvInputs: matchingHall.hasAvInputs,
            hasPc: matchingHall.hasPc,
            hasWhiteboard: matchingHall.hasWhiteboard,
            hasProjector: matchingHall.hasProjector,
            // Legacy format for backwards compatibility
            information: {
              av_inputs: matchingHall.hasAvInputs ? 'yes' : 'no',
              pc: matchingHall.hasPc ? 'yes' : 'no',
              whiteboard: matchingHall.hasWhiteboard ? 'yes' : 'no',
              projector: matchingHall.hasProjector ? 'yes' : 'no'
            },
            features: []
          });
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching room data:', error);
        setLoading(false);
      }
    };

    fetchRoomData();
  }, [building, room]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center pt-20">
        <div className="w-8 h-8 border-4 border-white/20 border-t-purple-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!roomData) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white pt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <p className="text-xl text-center text-gray-400">Room not found</p>
        </div>
      </div>
    );
  }

  const nextImage = () => {
    setCurrentImageIndex((prev) => 
      prev === (roomData.photos?.length || 1) - 1 ? 0 : prev + 1
    );
  };

  const previousImage = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? (roomData.photos?.length || 1) - 1 : prev - 1
    );
  };

  const TechAmenity = ({ label, available, Icon }) => (
    <div className={`flex items-center gap-3 p-3 rounded-xl border transition-all duration-200 ${
      available 
        ? 'bg-white/5 border-white/10 text-white' 
        : 'bg-white/5 border-white/5 text-gray-500 opacity-50'
    }`}>
      <Icon className={`w-5 h-5 ${available ? 'text-purple-400' : 'text-gray-600'}`} />
      <span className="text-sm font-medium">{label}</span>
      {available && (
        <div className="ml-auto w-2 h-2 rounded-full bg-green-500 shadow-[0_0_10px_rgba(34,197,94,0.5)]" />
      )}
    </div>
  );

  const hasInfo = roomData.information && (
    roomData.information.pc || 
    roomData.information.projector || 
    roomData.information.av_inputs || 
    roomData.information.whiteboard
  );

  const hasFeatures = roomData.features && roomData.features.length > 0;

  return (
    <div className="min-h-screen bg-[#0a0a0a] pt-24 pb-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Navigation */}
        <button 
          onClick={() => navigate(-1)}
          className="group flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 transition-transform group-hover:-translate-x-1" />
          <span>Back to rooms</span>
        </button>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            {roomData.building} <span className="text-purple-400">/</span> {roomData.room}
          </h1>
          <p className="text-gray-400 text-lg">{roomData.campus}</p>
        </div>

        <div className="space-y-8">
          {/* Image Gallery */}
          {roomData.photos && roomData.photos.length > 0 && (
            <div className="relative rounded-2xl overflow-hidden bg-[#111] border border-white/10 aspect-video">
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={currentImageIndex}
                  className="absolute inset-0"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <img
                    src={roomData.photos[currentImageIndex]}
                    alt={`${roomData.building} ${roomData.room}`}
                    className="w-full h-full object-cover"
                  />
                </motion.div>
              </AnimatePresence>

              {/* Navigation Arrows */}
              {roomData.photos.length > 1 && (
                <>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none" />
                  <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex justify-between px-4">
                    <button
                      onClick={previousImage}
                      className="p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors backdrop-blur-sm"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                    <button
                      onClick={nextImage}
                      className="p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors backdrop-blur-sm"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>
                  </div>
                  {/* Dots Indicator */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                    {roomData.photos.map((_, idx) => (
                      <div
                        key={idx}
                        className={`w-1.5 h-1.5 rounded-full transition-all ${
                          idx === currentImageIndex ? 'bg-white w-3' : 'bg-white/50'
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Features Column */}
            <div className="lg:col-span-1 space-y-6">
              {/* Tech Amenities */}
              {roomData.information && (
                <div className="bg-[#111] rounded-2xl border border-white/10 p-6">
                  <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                    <Monitor className="text-purple-400 w-5 h-5" />
                    Equipment
                  </h2>
                  <div className="space-y-3">
                    <TechAmenity 
                      label="Computer" 
                      available={roomData.hasPc || roomData.information.pc === 'yes'} 
                      Icon={Monitor}
                    />
                    <TechAmenity 
                      label="Projector" 
                      available={roomData.hasProjector || roomData.information.projector === 'yes'} 
                      Icon={Projector}
                    />
                    <TechAmenity 
                      label="AV Inputs" 
                      available={roomData.hasAvInputs || roomData.information.av_inputs === 'yes'} 
                      Icon={Cable}
                    />
                    <TechAmenity 
                      label="Whiteboard" 
                      available={roomData.hasWhiteboard || roomData.information.whiteboard === 'yes'} 
                      Icon={SquarePen}
                    />
                  </div>
                </div>
              )}

              {/* General Features */}
              {hasFeatures && (
                <div className="bg-[#111] rounded-2xl border border-white/10 p-6">
                  <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                    <MdWork className="text-purple-400 w-5 h-5" />
                    Room Features
                  </h2>
                  <div className="space-y-3">
                    {roomData.features.map((feature, index) => {
                      const IconComponent = featureIcons[feature];
                      return (
                        <div
                          key={index}
                          className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/5"
                        >
                          {IconComponent && <IconComponent className="w-5 h-5 text-gray-400" />}
                          <span className="text-gray-300 text-sm font-medium">{feature}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {!hasInfo && !hasFeatures && (
                <div className="bg-[#111] rounded-2xl border border-white/10 p-6">
                  <p className="text-gray-500 italic text-center">No specific amenities listed</p>
                </div>
              )}
            </div>

            {/* Schedule Column */}
            <div className="lg:col-span-2">
              <div className="bg-[#111] rounded-2xl border border-white/10 p-6 h-full">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                  <h2 className="text-xl font-bold text-white">Weekly Schedule</h2>
                  <div className="flex gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                      <span className="text-gray-400">Available</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-red-500"></div>
                      <span className="text-gray-400">Occupied</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white/5 rounded-xl p-1 overflow-hidden">
                  <DynamicSchedule 
                    building={decodeURIComponent(building).trim()} 
                    room={decodeURIComponent(room).trim()} 
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoomDetails;
